/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TableCreateForm
 *
 * React component to create new application tables with full PostgreSQL-compliant validations and relationship configuration.
 * Features:
 * - Dynamic add/remove fields/columns
 * - Enforced field and table name rules (PostgreSQL standards)
 * - Display/description/regex/relationship validator logic
 * - LookupDialog for related table selection (per-row)
 * - Live UI feedback and comments everywhere
 * - JSON output preview on submit
 * - Display Name is MANDATORY at table and column level
 */
import React, { useState, useEffect } from "react";
import { useTheme } from "../theme/ThemeContext";
import Button from "../components/Button";
import LookupDialog from "../components/common/LookupDialog";
import APIHelper from "../api/APIHelper";
import { useParams } from 'react-router-dom';
import envConfig from "../config/envConfig";
import { useAlertDialog } from "../components/common/AlertDialogContext";
import GradientBar from "../components/common/GradientBar";
import ProgressBarDialog from "../components/common/ProgressBar";
import ThemedToggle from "../components/common/ThemedToggle";
import { retrieve } from "../utils/storageHelper";
import { handleErrorResponse } from "../utils/utils";
import { useNavigate } from "react-router-dom";
import FieldUiRulesDialog from "../components/applications/FieldUiRulesDialog";
import TableSettingsPanel from "../components/applications/TableSettingsPanel";
import { useQuery } from '@tanstack/react-query';
import UIRulesAssistant from "../components/applications/UIRulesAssistant";
import FieldUIRulesAssistant from "../components/applications/FieldUIRulesAssistant";
import {
    isPgNameValid,
    isDisplayNameValid,
    isDescriptionValid,
    isValidRegex,
    cleanObject
} from "../utils/validations";

// Supported field types, data types, relationships
const DATA_TYPES = [
    { value: "string", label: "String (VARCHAR)" },
    { value: "text", label: "Text" },
    { value: "integer", label: "Integer" },
    { value: "float", label: "Float" },
    { value: "decimal", label: "Decimal" },
    { value: "date", label: "Date" },
    { value: "datetime", label: "DateTime" },
    { value: "boolean", label: "Boolean" },
    { value: "image", label: "Image" },
    { value: "file", label: "File" },
    { value: "enum", label: "Enum" },
    { value: "json", label: "JSON" },
    { value: "geo_point", label: "Geographic Point" }
];
const RELATIONSHIP_TYPES = [
    { value: "fk", label: "Foreign Key" },
    { value: "one-to-one", label: "One to One" },
    { value: "one-to-many", label: "One to Many" },
    // { value: "many-to-many", label: "Many to Many" },
    { value: "self", label: "Self Reference" }
];
const DELETE_CASCADE_OPTIONS = [
    { value: "CASCADE", label: "CASCADE - Delete referencing rows" },
    { value: "SET NULL", label: "SET NULL - Set to null" },
    { value: "RESTRICT", label: "RESTRICT - Prevent deletion if referenced" },
    { value: "NO ACTION", label: "NO ACTION - Deferred constraint check" }
];
const FIELD_TYPES = [
    { value: "dataType", label: "Data Type" },
    { value: "relation", label: "Relation" }
];

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});


/**
 * Returns True is the Dictionary is not empty
 * @param {*} obj 
 * @returns 
 */
function isNonEmptyObject(obj) {
    return obj && typeof obj === "object" && Object.keys(obj).length > 0;
}


export default function TableCreateForm() {
    const { id } = useParams();
    const [uiRulesDialogOpen, setUiRulesDialogOpen] = useState(false);
    const [uiRulesFieldIndex, setUiRulesFieldIndex] = useState(null);
    const [roles, setRoles] = useState([]);

    const tablesQueryKey = ['applicationTables', id];
    const rolesQueryKey = ['applicationRoles', id];

    const {
        data: tablesQueryData,
        error: tablesQueryError,
        isLoading: tablesQueryLoading,
        isFetching: tablesQueryFetching,
    } = useQuery({
        queryKey: tablesQueryKey,
        queryFn: async ({ signal }) => {
            if (!id) return { results: [], count: 0 };
            const url = `/arcee/application/table/list/?application_id=${id}&page_size=5000`;
            const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
            if (!resp.ok) {
                let j = {};
                try { j = await resp.json(); } catch (e) { /* ignore */ }
                throw new Error(j?.error || `Failed to fetch tables (${resp.status})`);
            }
            return resp.json();
        },
        enabled: !!id,
        onSuccess: (data) => {
            setRoles(data?.results ?? []);
        },
        staleTime: 60_000,
        retry: 1,
    });

    const {
        data: rolesQueryData,
        error: rolesQueryError,
        isLoading: rolesQueryLoading,
        isFetching: rolesQueryFetching,
    } = useQuery({
        queryKey: rolesQueryKey,
        queryFn: async ({ signal }) => {
            if (!id) return { results: [] };
            const url = `/arcee/user/roles/${id}/?page_size=1000`;
            const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
            if (!resp.ok) {
                let j = {};
                try { j = await resp.json(); } catch (e) { /* ignore */ }
                throw new Error(j?.error || `Failed to fetch roles (${resp.status})`);
            }
            return resp.json();
        },
        enabled: !!id,
        staleTime: 60_000,
        retry: 1,
    });

    const openUiRulesDialog = (idx) => {
        setUiRulesFieldIndex(idx);
        setUiRulesDialogOpen(true);
    };


    const closeUiRulesDialog = () => {
        setUiRulesDialogOpen(false);
        setUiRulesFieldIndex(null);
    };

    const handleSaveUiRules = (uiRules) => {
        if (uiRulesFieldIndex == null) return;
        const updated = [...fields];
        updated[uiRulesFieldIndex] = {
            ...updated[uiRulesFieldIndex],
            ui_rules: uiRules,               // attach ui_rules to that field
        };
        console.log("UI Rules" + JSON.stringify(uiRules));
        setFields(updated);
    };


    const { theme } = useTheme();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [table, setTable] = useState({
        name: "",
        display_name: "",
        description: "",
        representative_image: "",
        record_identifier: "",
        visibility: "private",
        soft_delete: false,
        pk_strategy: "serial",
        syncable: false,
        is_global: false,
        unique_constraints: [],
        check_constraints: [],
        composite_indexes: [],
        read_filter: null,
    });

    // UI Rules State (for Assistant)
    const [tableUiRules, setTableUiRules] = useState({});
    const [showAssistant, setShowAssistant] = useState(false);

    // Toggle for Field Rules: Dialog vs Assistant
    const [useFieldRulesAssistant, setUseFieldRulesAssistant] = useState(false);

    const [fields, setFields] = useState([getEmptyField()]);
    const [relatedTableDialogsOpen, setRelatedTableDialogsOpen] = useState([false]);
    const [error, setError] = useState("");
    const [constraintEditingOpen, setConstraintEditingOpen] = useState(false);
    const [success, setSuccess] = useState("");
    const [jsonResult, setJsonResult] = useState("");
    const [loading, setLoading] = useState(false);
    const [availableTables, setAvailableTables] = useState([]);
    const [tableError, setTableError] = useState(null);

    /** Generates empty field/column object */
    function getEmptyField() {
        return {
            field_type: "dataType",
            name: "",
            display_name: "",
            data_type: "",
            max_length: "",
            numeric_precision: "",
            numeric_scale: "",
            required: false,
            hint: "",
            // allowed_characters: "",
            // allowed_characters_error: "",
            related_table_name: "",
            relation_type: "",
            on_delete_rule: "CASCADE", // Default to CASCADE for FK relations
            allowed_options: "",
            allowed_options_error: "",
            is_unique: false, // Initialize unique flag
            indexed: false, // Create a DB index on this column
            editable_after_create: true, // E-10: allow updates after insert
            ui_rules: {} // set dynamically via FieldUiRulesDialog
        };
    }

    useEffect(() => {
        if (tablesQueryError) {
            setTableError(tablesQueryError?.message || String(tablesQueryError));
            setAvailableTables([]);
        } else if (tablesQueryData) {
            setAvailableTables(Array.isArray(tablesQueryData.results) ? tablesQueryData.results : []);
            setTableError(null);
        }

        if (rolesQueryError) {
            setRoles([]);
        } else if (rolesQueryData) {
            const opts = Array.isArray(rolesQueryData.results)
                ? rolesQueryData.results.map((r) => ({ value: r.name, label: r.label || r.name }))
                : [];
            setRoles(opts);
        }
    }, [tablesQueryData, tablesQueryError, rolesQueryData, rolesQueryError]);

    /** Add new blank column/field and keep dialog states synced. */
    function handleAddField() {
        setFields(f => [...f, getEmptyField()]);
        setRelatedTableDialogsOpen(arr => [...arr, false]);
    }
    /** Remove a column/field and keep dialog states synced. */
    function handleRemoveField(index) {
        if (fields.length > 1) {
            setFields(flds => flds.filter((_, i) => i !== index));
            setRelatedTableDialogsOpen(arr => arr.filter((_, i) => i !== index));
        }
    }
    /** Set open/close state for per-row LookupDialog. */
    function setDialogOpen(index, open) {
        setRelatedTableDialogsOpen(arr => {
            const copy = [...arr];
            copy[index] = open;
            return copy;
        });
    }
    /** Update a single field/column - with all relevant validators and live restrictions. */
    function handleFieldChange(index, key, value) {
        setFields(flds =>
            flds.map((field, i) => {
                if (i !== index) return field;
                // if (key === "allowed_characters") {
                //     return {
                //         ...field,
                //         allowed_characters: value,
                //         allowed_characters_error: isValidRegex(value)
                //             ? ""
                //             : "Invalid regular expression. Please use correct regex syntax."
                //     };
                // }
                if (key === "field_type") {
                    return {
                        ...getEmptyField(),
                        field_type: value // reset everything except type
                    };
                }
                if (key === "data_type") {
                    return {
                        ...field,
                        data_type: value,
                        // allowed_characters: "",
                        // allowed_characters_error: "",
                        max_length: "",
                        numeric_precision: "",
                        numeric_scale: ""
                    };
                }
                if (key === "name") {
                    let v = value.replace(/[^a-z0-9_]/g, "").toLowerCase();
                    if (v.length > 63) v = v.slice(0, 63);
                    return { ...field, name: v };
                }
                if (key === "display_name") {
                    let v = value.replace(/[^a-zA-Z0-9 ]/g, "");
                    return { ...field, display_name: v };
                }
                if (key === "hint") {
                    return { ...field, hint: value };
                }
                if (key === "allowed_options") {
                    const raw = value;
                    const values = raw.split(",").map(v => v.trim());

                    let error = "";

                    if (values.some(v => v === "")) {
                        error = "Empty values are not allowed.";
                    } else if (values.some(v => v.length > 64)) {
                        error = "Each value must be ≤ 64 characters.";
                    }

                    return {
                        ...field,
                        allowed_options: raw,
                        allowed_options_error: error
                    };
                }
                if (key === "relation_type") {
                    return {
                        ...field,
                        relation_type: value,
                        // Reset on_delete_rule to default when switching to FK, clear when switching away
                        on_delete_rule: value === "fk" ? "CASCADE" : ""
                    };
                }

                return { ...field, [key]: value };
            })
        );
    }
    /** Update state for top-level table metadata fields. */
    function handleTableChange(key, value) {
        if (key === "name") {
            let v = value.replace(/[^a-z0-9_]/g, "").toLowerCase();
            if (v.length > 63) v = v.slice(0, 63);
            setTable(t => ({ ...t, [key]: v }));
        } else if (key === "display_name") {
            let v = value.replace(/[^a-zA-Z0-9 ]/g, "");
            setTable(t => ({ ...t, [key]: v }));
        } else if (key === "description") {
            let v = value.slice(0, 500);
            if (isDescriptionValid(v)) setTable(t => ({ ...t, [key]: v }));
        } else {
            setTable(t => ({ ...t, [key]: value }));
        }
    }
    /**
     * Validate, clean, and generate final JSON; update UI feedback or show error.
     * Display Name is now MANDATORY at table and column level.
     */
    async function handleSubmit(e) {
        e.preventDefault();
        if (constraintEditingOpen) {
            setError("Finish or cancel the constraint/index you're editing before creating the table.");
            return;
        }
        setError("");
        setSuccess("");
        setJsonResult("");
        if (!table.name.trim()) {
            setError("Table name is required.");
            return;
        }
        if (!isPgNameValid(table.name.trim())) {
            setError(
                "Table name must be 1-63 chars, start with letter/underscore, only lowercase letters, numbers or underscores, and not be a PostgreSQL reserved keyword."
            );
            return;
        }
        if (!table.display_name.trim()) {
            setError("Table Display Name is required.");
            return;
        }
        if (!isDisplayNameValid(table.display_name)) {
            setError("Table Display Name can only contain alphabets, numbers, and spaces.");
            return;
        }
        if (table.description && !isDescriptionValid(table.description)) {
            setError("Table Description can only contain alphabets, numbers, and spaces.");
            return;
        }
        // Columns
        for (let [idx, field] of fields.entries()) {
            if (!field.name || !isPgNameValid(field.name)) {
                setError(
                    `Each column name must be 1-63 chars, start with a letter or underscore, contain only lowercase letters, numbers or underscores, and not be a PostgreSQL reserved keyword. (Check column #${idx + 1})`
                );
                return;
            }
            if (!field.display_name.trim()) {
                setError(`Display Name is required for all columns. (Check column #${idx + 1})`);
                return;
            }
            if (!isDisplayNameValid(field.display_name)) {
                setError(
                    "All Field Display Names can only contain alphabets, numbers, and spaces."
                );
                return;
            }
            if (field.data_type === "enum") {
                const raw = field.allowed_options || "";
                const values = raw.split(",").map(v => v.trim());

                if (!raw.trim()) {
                    setError(`Enum options cannot be empty (Column #${idx + 1}).`);
                    return;
                }
                if (values.some(v => v === "")) {
                    setError(`Enum contains empty values (Column #${idx + 1}).`);
                    return;
                }
                if (values.some(v => v.length > 64)) {
                    setError(`Each enum value must be ≤ 64 characters (Column #${idx + 1}).`);
                    return;
                }
            }

            // Validate on_delete_rule constraints
            if (field.field_type === "relation" && field.relation_type === "fk") {
                if (field.on_delete_rule === "SET NULL" && field.required) {
                    setError(
                        `Column #${idx + 1}: "SET NULL" delete rule requires the field to be optional (uncheck Required).`
                    );
                    return;
                }
            }

            // if (field.allowed_characters_error) {
            //     setError("All regular expressions for Allowed Characters must be valid.");
            //     return;
            // }
        }
        if (fields.length < 1) {
            setError("At least one column is required.");
            return;
        }
        if (!table.record_identifier.trim()) {
            setError("Please select a Record Identifier field.");
            return;
        }
        // Optionally check if record_identifier is a valid field name in fields:
        const validIdentifier = fields.some(f => f.name === table.record_identifier);
        if (!validIdentifier) {
            setError("Selected Record Identifier is not a valid field.");
            return;
        }
        // Output serialization
        const cleanedFields = fields.map(field => {
            const isRelation = field.field_type === "relation";
            const out = {};
            if (field.name) out.name = field.name;
            if (field.display_name) out.display_name = field.display_name;
            if (isRelation) {
                if (field.related_table_name) out.related_table_name = field.related_table_name;
                if (field.relation_type) out.data_type = field.relation_type;

                // Include on_delete_rule only for FK relations
                if (field.relation_type === "fk" && field.on_delete_rule) {
                    out.on_delete_rule = field.on_delete_rule;
                }
            } else {
                if (field.data_type === "enum") {
                    out.data_type = "enum";
                    out.allowed_options = field.allowed_options;
                } else {
                    if (field.data_type) out.data_type = field.data_type;
                }
                if (field.data_type === "string" && field.max_length) out.max_length = Number(field.max_length);
                if (field.numeric_precision) out.numeric_precision = Number(field.numeric_precision);
                if (field.numeric_scale) out.numeric_scale = Number(field.numeric_scale);
                if ("required" in field) out.required = !!field.required;
                if ("is_unique" in field) out.is_unique = !!field.is_unique;
                out.indexed = !!field.indexed;
                // if (field.allowed_characters) out.allowed_characters = field.allowed_characters;
                if (isNonEmptyObject(field.ui_rules)) out.ui_rules = field.ui_rules;
            }
            if (field.hint) out.hint = field.hint;
            out.editable_after_create = field.editable_after_create !== false;
            return cleanObject(out);
        });
        const tableMeta = {
            name: table.name,
            description: table.description,
            visibility: table.visibility,
            ...(table.display_name ? { display_name: table.display_name } : {}),
            ...(table.representative_image ? { representative_image: table.representative_image } : {}),
            fields: cleanedFields,
            record_identifier: table.record_identifier,
            ui_rules: isNonEmptyObject(tableUiRules) ? tableUiRules : undefined,
            soft_delete: !!table.soft_delete,
            pk_strategy: table.pk_strategy || "serial",
            syncable: !!table.syncable,
            is_global: !!table.is_global,
            unique_constraints: table.unique_constraints || [],
            check_constraints: table.check_constraints || [],
            composite_indexes: table.composite_indexes || [],
            read_filter: table.read_filter || undefined,
        };

        const result = {
            application_id: parseInt(id, 10),
            table_metadata: cleanObject(tableMeta)
        };
        // setJsonResult(JSON.stringify(result, null, 2));
        console.log("Creating Table Request Payload: " + JSON.stringify(result, null, 2));

        // Show progress bar and call API
        setLoading(true);
        try {
            const response = await apiHelper.apiCall('/arcee/application/table/create/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(result)
            });

            const text = await response.text();

            if (!response.ok) {
                await handleErrorResponse(response);
                return;
            }

            showAlertDialog({
                title: "Success",
                message: "Table created successfully!",
                type: "success",
                okLabel: "OK",
                showCancel: false,
                onOk: () => {
                    navigate(`/dashboard/applications/${id}/`);
                }
            });

        } catch (error) {
            showAlertDialog({
                title: "Could not create table",
                message: error.message,
                type: "error",
                okLabel: "OK",
                showCancel: false,
                onOk: () => {
                    // // Reset the main table form state to initial empty values
                    // setTable({ name: "", display_name: "", description: "", representative_image: "", record_identifier: ""});
                    // // Reset fields array to have a single empty field
                    // setFields([getEmptyField()]);
                    // // Reset any related dialog states if needed
                    // setRelatedTableDialogsOpen([false]);
                    // // Clear any messages or errors shown
                    // setError("");
                    // setSuccess("");
                    // setJsonResult("");
                }
            });
        } finally {
            setLoading(false);
        }
    }

    // --- RENDER FORM ---
    return (
        <div style={{ maxWidth: 800, margin: "52px auto", fontFamily: theme.fontFamily }}>
            {/* Top gradient bar */}
            <GradientBar height={10} />
            <ProgressBarDialog open={loading} text_message="Creating Table..." cancellable={false} />
            <form onSubmit={handleSubmit} style={{
                background: "#fff",
                borderRadius: theme.cardRadius,
                boxShadow: "0 4px 24px rgba(65,70,150,0.13)",
                padding: "36px 20px 23px 20px",
                position: "relative" // Ensure relative positioning for absolute elements if needed
            }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
                    <h2 style={{ color: theme.primary, fontWeight: 700, margin: 0 }}>
                        Create Table
                    </h2>
                    <button
                        type="button"
                        onClick={() => setShowAssistant(true)}
                        style={{
                            background: theme.secondary,
                            color: "#fff",
                            border: "none",
                            borderRadius: 20,
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: "0.9rem",
                            cursor: "pointer",
                            transition: "all 0.2s"
                        }}
                    >
                        <i className="bi bi-robot" />
                        UI Rules
                    </button>
                </div>
                {error && (
                    <div style={{ color: "#d53b3b", fontWeight: 600, marginBottom: 15 }}>{error}</div>
                )}
                {success && (
                    <div style={{ color: "#1cb05c", fontWeight: 600, marginBottom: 15 }}>{success}</div>
                )}

                {/* Table metadata */}
                <div className="mb-4">
                    <label style={{ color: theme.primary, fontWeight: 600 }}>Table Name*</label>
                    <input
                        className="form-control"
                        value={table.name}
                        onChange={e => handleTableChange("name", e.target.value)}
                        maxLength={63}
                        required
                        placeholder="table_name"
                        style={{ borderRadius: theme.buttonRadius }}
                    />
                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                        <i className="bi bi-info-circle" /> 1–63 chars, only lowercase letters, numbers, underscores.
                        Must start with a letter or underscore. No reserved keywords. Name as stored in the Database.
                    </div>
                </div>
                <div className="mb-4">
                    <label style={{ color: theme.primary, fontWeight: 600 }}>Display Name*</label>
                    <input
                        className="form-control"
                        value={table.display_name}
                        onChange={e => handleTableChange("display_name", e.target.value)}
                        maxLength={100}
                        required
                        placeholder="Table Display Name"
                        style={{ borderRadius: theme.buttonRadius }}
                    />
                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                        <i className="bi bi-info-circle" /> <b>This field is required.</b> Alphabets, numbers, and spaces only. Name used for display purposes as seen in the application.
                    </div>
                </div>
                <div className="mb-4">
                    <label style={{ color: theme.primary, fontWeight: 600 }}>Description</label>
                    <textarea
                        className="form-control"
                        value={table.description}
                        onChange={e => handleTableChange("description", e.target.value)}
                        rows={2}
                        placeholder="Table Description (max 500 characters)"
                        maxLength={500}
                        style={{ borderRadius: theme.buttonRadius }}
                    />
                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                        <i className="bi bi-info-circle" /> Alphabets, numbers, and spaces only. {table.description.length}/500 characters. Description about the table.
                    </div>
                </div>
                {/* Visibility */}
                <div className="mb-4">
                    <label style={{ color: theme.primary, fontWeight: 600 }}>Visibility</label>
                    <select
                        className="form-control"
                        value={table.visibility}
                        onChange={e => handleTableChange("visibility", e.target.value)}
                        style={{ borderRadius: theme.buttonRadius }}
                    >
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                        <option value="hidden">Hidden</option>
                    </select>
                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                        <i className="bi bi-info-circle" /> Control access: Public (Everyone), Private (Authorized), Hidden (Admin only).
                    </div>
                </div>

                <h4 style={{ color: theme.primary, fontWeight: 600, margin: "22px 0 16px 0" }}>
                    Columns
                </h4>
                {fields.map((field, idx) => {
                    const showDataType = field.field_type === "dataType";
                    const showRelation = field.field_type === "relation";
                    // const showAllowedChars = showDataType && (field.data_type === "string" || field.data_type === "text");

                    // For relation: only name is required.
                    // For dataType: both name and data_type are required.
                    const uiButtonsDisabled =
                        field.field_type === "relation"
                            ? true
                            : !field.name || !field.data_type;

                    const isRelationType =
                        field.field_type === "relation" ||
                        ["fk", "one-to-one", "one-to-many", "self"].includes(field.data_type);


                    return (
                        <div key={idx} style={{
                            border: `1.5px solid ${theme.primary}`,
                            borderRadius: theme.cardRadius,
                            padding: 16,
                            marginBottom: 18,
                            background: idx % 2 ? "#fcfcff" : "#f7f8fb"
                        }}>


                            <div /* card wrapper */>
                                <div className="d-flex justify-content-end mb-2">
                                    <div className="d-flex flex-column align-items-end" style={{ gap: 6 }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            onClick={() => openUiRulesDialog(idx)}
                                            disabled={uiButtonsDisabled}
                                            /* styles unchanged */
                                            title={
                                                uiButtonsDisabled
                                                    ? field.field_type === "relation"
                                                        ? "Enter Column Name to configure UI Rules."
                                                        : "Enter Column Name and Data Type to configure UI Rules."
                                                    : "Configure UI Rules for this field."
                                            }
                                        >
                                            <i className="bi bi-sliders" />
                                            <span style={{ fontSize: 12 }}> UI Rules</span>
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-sm d-none"
                                            disabled={uiButtonsDisabled}
                                            /* styles unchanged */
                                            title={
                                                uiButtonsDisabled
                                                    ? field.field_type === "relation"
                                                        ? "Enter Column Name to configure Validation Rules."
                                                        : "Enter Column Name and Data Type to configure Validation Rules."
                                                    : "Configure Validation Rules for this field."
                                            }
                                        >
                                            <i className="bi bi-shield-check" />
                                            <span style={{ fontSize: 12 }}> Validation Rules</span>
                                        </button>
                                    </div>
                                </div>
                                {/* ...rest of column card */}
                            </div>

                            {/* Field Type */}
                            <div className="mb-4">
                                <label style={{ color: theme.primary, fontWeight: 600 }}>Field Type*</label>
                                <select
                                    className="form-control"
                                    style={{ borderRadius: theme.buttonRadius }}
                                    value={field.field_type}
                                    onChange={e => handleFieldChange(idx, "field_type", e.target.value)}
                                    required
                                >
                                    <option value="">Choose...</option>
                                    {FIELD_TYPES.map(t =>
                                        <option value={t.value} key={t.value}>{t.label}</option>
                                    )}
                                </select>
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> Choose if column specifies a data type or a relation.
                                </div>
                            </div>
                            {/* Column Name */}
                            <div className="mb-4">
                                <label style={{ color: theme.primary, fontWeight: 600 }}>Column Name*</label>
                                <input
                                    className="form-control"
                                    value={field.name}
                                    onChange={e => handleFieldChange(idx, "name", e.target.value)}
                                    required
                                    placeholder="column_name"
                                    maxLength={63}
                                    style={{ borderRadius: theme.buttonRadius }}
                                />
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> 1–63 chars, only lowercase letters, numbers, underscores. Must start with a letter or underscore. No reserved keywords.
                                </div>
                            </div>
                            {/* Display Name */}
                            <div className="mb-4">
                                <label style={{ color: theme.primary, fontWeight: 600 }}>Display Name*</label>
                                <input
                                    className="form-control"
                                    value={field.display_name}
                                    onChange={e => handleFieldChange(idx, "display_name", e.target.value)}
                                    maxLength={100}
                                    required
                                    placeholder="Display Name"
                                    style={{ borderRadius: theme.buttonRadius }}
                                />
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> <b>This field is required.</b> Alphabets, numbers, and spaces only. Name used for Display purposes as seen in the application.
                                </div>
                            </div>
                            {/* DATA TYPE */}
                            {showDataType && (
                                <div className="mb-4">
                                    {/* Data Type */}
                                    <label style={{ color: theme.primary, fontWeight: 600 }}>Data Type*</label>
                                    <select
                                        className="form-control"
                                        style={{ borderRadius: theme.buttonRadius }}
                                        value={field.data_type}
                                        onChange={e => handleFieldChange(idx, "data_type", e.target.value)}
                                        required
                                    >
                                        <option value="">Choose...</option>
                                        {DATA_TYPES.map(t =>
                                            <option value={t.value} key={t.value}>{t.label}</option>
                                        )}
                                    </select>
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Data Type of the Table.
                                    </div>
                                    {field.data_type === "string" && (
                                        <div className="mb-4">
                                            <label style={{ color: theme.primary, fontWeight: 600 }}>Max Length</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                style={{ borderRadius: theme.buttonRadius }}
                                                value={field.max_length || ""}
                                                onChange={e => {
                                                    let val = e.target.value;
                                                    if (val > 255) val = 255;  // enforce max 255 programmatically
                                                    handleFieldChange(idx, "max_length", val);
                                                }}
                                                min={1}
                                                max={255}
                                                placeholder="e.g. 255"
                                            />
                                            <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                                <i className="bi bi-info-circle" />1-255 Chars
                                            </div>
                                        </div>

                                    )}
                                    {field.data_type === "decimal" && (
                                        <div className="mb-4">
                                            <label style={{ color: theme.primary, fontWeight: 600 }}>Precision</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min={1}
                                                style={{ borderRadius: theme.buttonRadius }}
                                                value={field.numeric_precision || ""}
                                                onChange={e => handleFieldChange(idx, "numeric_precision", e.target.value)}
                                                placeholder="e.g. 10"
                                            />
                                            <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                                <i className="bi bi-info-circle" /> Precision is the total number of digits (default is 10). <b>Example: </b>12345.67 has precision 7.<br /><span style={{ color: "#555" }}>Layman: How many digits in total, before and after decimal point.</span>
                                            </div>
                                            <label style={{ color: theme.primary, fontWeight: 600, marginTop: 10 }}>Scale</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min={0}
                                                style={{ borderRadius: theme.buttonRadius }}
                                                value={field.numeric_scale || ""}
                                                onChange={e => handleFieldChange(idx, "numeric_scale", e.target.value)}
                                                placeholder="e.g. 2"
                                            />
                                            <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                                <i className="bi bi-info-circle" /> Scale is how many digits after the decimal point (default is 0). <b>Example: </b>123.45 has scale 2.<br /><span style={{ color: "#555" }}>Layman: How many digits after the decimal point.</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* {showAllowedChars && (
                                <div className="mb-4">
                                    <label style={{ color: theme.primary, fontWeight: 600 }}>Allowed Characters (Regex)</label>
                                    <input
                                        className="form-control"
                                        value={field.allowed_characters}
                                        onChange={e => handleFieldChange(idx, "allowed_characters", e.target.value)}
                                        placeholder="e.g. ^[a-zA-Z]+$"
                                        style={{ borderRadius: theme.buttonRadius }}
                                        maxLength={512}
                                    />
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Only applies to String/Text fields. This regular expression defines allowed characters for the column value.
                                    </div>
                                    {field.allowed_characters_error && (
                                        <div style={{ color: "#d53b3b", fontSize: 13, marginTop: 2 }}>
                                            {field.allowed_characters_error}
                                        </div>
                                    )}
                                </div>
                            )} */}
                            {showRelation && (
                                <>
                                    <div className="mb-4">
                                        <label style={{ color: theme.primary, fontWeight: 600 }}>Related Table*</label>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <input
                                                className="form-control"
                                                value={field.related_table_name || ""}
                                                readOnly
                                                placeholder="Choose table..."
                                                onClick={() => setDialogOpen(idx, true)}
                                                style={{
                                                    cursor: "pointer",
                                                    background: "#f4f6fa",
                                                    borderRight: "none",
                                                    borderRadius: theme.buttonRadius
                                                }}
                                            />
                                            <button
                                                type="button"
                                                style={{
                                                    marginLeft: `5px`,
                                                    borderRadius: `${theme.buttonRadius}`,
                                                    border: `1.4px solid ${theme.primary}`,
                                                    background: theme.secondary,
                                                    color: "#fff",
                                                    fontSize: 17,
                                                    padding: "0 11px",
                                                    height: 38,
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => setDialogOpen(idx, true)}
                                            >
                                                <i className="bi bi-search" />
                                            </button>
                                        </div>
                                        <LookupDialog
                                            open={!!relatedTableDialogsOpen[idx]}
                                            title="Select Related Table"
                                            options={availableTables}
                                            onSelect={(selectedId) => {
                                                const selectedTable = availableTables.find(tbl => tbl.id === selectedId);
                                                if (selectedTable) {
                                                    handleFieldChange(idx, "related_table_name", selectedTable.name);
                                                }
                                                setDialogOpen(idx, false);
                                            }}
                                            onClose={() => setDialogOpen(idx, false)}
                                        />
                                        <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                            <i className="bi bi-info-circle" /> Choose table to relate to.
                                        </div>
                                        {tableError && <div style={{ color: "red", marginTop: 4 }}>{tableError}</div>}
                                    </div>
                                    <div className="mb-4">
                                        <label style={{ color: theme.primary, fontWeight: 600 }}>Relationship*</label>
                                        <select
                                            className="form-control"
                                            style={{ borderRadius: theme.buttonRadius }}
                                            value={field.relation_type}
                                            onChange={e => handleFieldChange(idx, "relation_type", e.target.value)}
                                            required={!!field.related_table_name}
                                        >
                                            <option value="">Select type...</option>
                                            {RELATIONSHIP_TYPES.map(t =>
                                                <option value={t.value} key={t.value}>{t.label}</option>
                                            )}
                                        </select>
                                    </div>

                                    {/* Delete Cascade Rule dropdown - only show for FK */}
                                    {field.relation_type === "fk" && (
                                        <div className="mb-4">
                                            <label style={{ color: theme.primary, fontWeight: 600 }}>
                                                Delete Cascade Rule*
                                            </label>
                                            <select
                                                className="form-control"
                                                style={{ borderRadius: theme.buttonRadius }}
                                                value={field.on_delete_rule || "CASCADE"}
                                                onChange={e => handleFieldChange(idx, "on_delete_rule", e.target.value)}
                                            >
                                                {DELETE_CASCADE_OPTIONS.map(opt =>
                                                    <option value={opt.value} key={opt.value}>{opt.label}</option>
                                                )}
                                            </select>
                                            <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                                <i className="bi bi-info-circle" /> Determines what happens to this field when the referenced record is deleted:
                                                <ul style={{ marginLeft: 12, marginTop: 4 }}>
                                                    <li><b>CASCADE:</b> Delete this record when referenced record is deleted</li>
                                                    <li><b>SET NULL:</b> Set field to null (requires optional field)</li>
                                                    <li><b>RESTRICT:</b> Prevent deletion if this record exists</li>
                                                    <li><b>NO ACTION:</b> Similar to RESTRICT (deferred check)</li>
                                                </ul>
                                            </div>
                                            {field.on_delete_rule === "SET NULL" && field.required && (
                                                <div style={{ color: "#d53b3b", fontSize: 13, marginTop: 4, fontWeight: 600 }}>
                                                    <i className="bi bi-exclamation-triangle" /> SET NULL requires this field to be optional. Please uncheck "Required" below.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            {field.data_type === "enum" && (
                                <div className="mb-4">
                                    <label style={{ color: theme.primary, fontWeight: 600 }}>Allowed Options (Enum)*</label>
                                    <input
                                        className="form-control"
                                        style={{ borderRadius: theme.buttonRadius }}
                                        placeholder="Comma separated. Example: draft,published,archived"
                                        value={field.allowed_options}
                                        onChange={e => handleFieldChange(idx, "allowed_options", e.target.value)}
                                        required
                                    />
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Enter comma-separated values. Each value must:
                                        <ul style={{ marginLeft: 12 }}>
                                            <li>Not be empty</li>
                                            <li>Not exceed 64 characters</li>
                                        </ul>
                                    </div>

                                    {field.allowed_options_error && (
                                        <div style={{ color: "#d53b3b", fontSize: 13 }}>{field.allowed_options_error}</div>
                                    )}
                                </div>
                            )}

                            <div className="mb-4">
                                <div style={{
                                    marginTop: 8,
                                    padding: "8px 12px",
                                    border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                    borderRadius: 8,
                                    background: "#fff",
                                    display: "inline-block",
                                    minWidth: 150
                                }}>
                                    <ThemedToggle
                                        checked={!!field.required}
                                        onChange={val => handleFieldChange(idx, "required", val)}
                                        onLabel="Required"
                                        offLabel="Optional"
                                    />
                                </div>
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> Toggle to set field as required or optional.
                                </div>
                            </div>

                            {/* Unique Checkbox */}
                            {!isRelationType && (
                                <div className="mb-4">
                                    <div style={{
                                        marginTop: 8,
                                        padding: "8px 12px",
                                        border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                        borderRadius: 8,
                                        background: "#fff",
                                        display: "inline-block",
                                        minWidth: 150
                                    }}>
                                        <ThemedToggle
                                            checked={!!field.is_unique}
                                            onChange={val => handleFieldChange(idx, "is_unique", val)}
                                            onLabel="Unique"
                                            offLabel="Duplicates Allowed"
                                        />
                                    </div>
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Toggle to enforce field values to be unique or to allow duplicates.
                                    </div>
                                </div>
                            )}

                            {/* Indexed toggle */}
                            {!isRelationType && (
                                <div className="mb-4">
                                    <div style={{
                                        marginTop: 8,
                                        padding: "8px 12px",
                                        border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                        borderRadius: 8,
                                        background: "#fff",
                                        display: "inline-block",
                                        minWidth: 150
                                    }}>
                                        <ThemedToggle
                                            checked={!!field.indexed}
                                            onChange={val => handleFieldChange(idx, "indexed", val)}
                                            onLabel="Indexed"
                                            offLabel="Not Indexed"
                                        />
                                    </div>
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Creates a B-Tree index on this field to speed up queries filtering by it.
                                    </div>
                                </div>
                            )}

                            {/* Editable after create toggle */}
                            <div className="mb-4">
                                <div style={{
                                    marginTop: 8,
                                    padding: "8px 12px",
                                    border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                    borderRadius: 8,
                                    background: "#fff",
                                    display: "inline-block",
                                    minWidth: 150
                                }}>
                                    <ThemedToggle
                                        checked={field.editable_after_create !== false}
                                        onChange={val => handleFieldChange(idx, "editable_after_create", val)}
                                        onLabel="Editable after creation"
                                        offLabel="Locked after creation"
                                    />
                                </div>
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> When off, this field cannot be updated once the record is created.
                                </div>
                            </div>

                            <div className="mb-4">
                                <label style={{ color: theme.primary, fontWeight: 600 }}>Hint</label>
                                <input
                                    className="form-control"
                                    style={{ borderRadius: theme.buttonRadius }}
                                    value={field.hint}
                                    onChange={e => handleFieldChange(idx, "hint", e.target.value)}
                                    placeholder="Short description/hint for this field"
                                    maxLength={80}
                                />
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> This text will be displayed in Create Form of this table.
                                </div>
                            </div>
                            {fields.length > 1 && (
                                <Button
                                    type="button"
                                    mode="secondary"
                                    onClick={() => handleRemoveField(idx)}
                                    style={{ padding: "5px 18px" }}
                                >
                                    <i className="bi bi-trash" /> Remove Column
                                </Button>
                            )}
                        </div>
                    );
                })}
                <div className="mb-3 text-end">
                    <Button type="button" mode="outlined" onClick={handleAddField}>
                        <i className="bi bi-plus-lg" /> Add Column
                    </Button>
                </div>
                <div className="mb-5">
                    <label style={{ color: theme.primary, fontWeight: 600 }}>Record Identifier*</label>
                    <select
                        className="form-control"
                        style={{ borderRadius: theme.buttonRadius }}
                        value={table.record_identifier}
                        onChange={e => handleTableChange("record_identifier", e.target.value)}
                        required
                        onInvalid={(e) => e.target.setCustomValidity(' ')}  // <-- remove tooltip
                        onInput={(e) => e.target.setCustomValidity('')}
                    >
                        <option value="">Select a field...</option>
                        {fields
                            .filter(f => f.name) // only fields with valid names show
                            .map((field, idx) => (
                                <option key={idx} value={field.name}>
                                    {field.display_name}
                                </option>
                            ))}
                    </select>
                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                        <i className="bi bi-info-circle" /> <b>This field is required and should be selected after the columns are defined.</b> It specifies which column will be used to display each record's name or label in selections. Select which field will be used as the record identifier.
                    </div>
                </div>

                {/* ── Schema Settings (soft delete + composite constraints/indexes) ── */}
                <TableSettingsPanel
                    table={table}
                    onChange={handleTableChange}
                    isEditMode={false}
                    showMetadata={false}
                    availableColumns={fields
                        .filter(f => f.name?.trim())
                        .map(f => ({ name: f.name, data_type: f.data_type || "string" }))}
                    hasRows={false}
                    onEditingChange={setConstraintEditingOpen}
                />

                <div className="d-flex justify-content-end mt-3">
                    <Button type="submit" mode="bordered">
                        Create Table
                    </Button>
                </div>
                {jsonResult && (
                    <pre
                        style={{
                            background: "#f6f8fa",
                            border: `1px solid ${theme.primary}`,
                            borderRadius: theme.cardRadius,
                            padding: "14px",
                            marginTop: "24px",
                            maxWidth: 790,
                            color: "#333",
                            fontSize: 16
                        }}
                    >
                        {jsonResult}
                    </pre>
                )}

            </form>
            {uiRulesDialogOpen && uiRulesFieldIndex != null && (useFieldRulesAssistant ? (
                <FieldUIRulesAssistant
                    open={uiRulesDialogOpen}
                    field={fields[uiRulesFieldIndex]}
                    fields={fields}
                    roles={roles}
                    required={fields[uiRulesFieldIndex]?.required === true}
                    onClose={closeUiRulesDialog}
                    onSave={handleSaveUiRules}
                />
            ) : (
                <FieldUiRulesDialog
                    open={uiRulesDialogOpen}
                    field={fields[uiRulesFieldIndex]}
                    fields={fields}
                    roles={roles}                 // can be [] – dialog handles it gracefully
                    required={fields[uiRulesFieldIndex]?.required === true}  // pass required flag
                    onClose={closeUiRulesDialog}
                    onSave={handleSaveUiRules}
                />
            ))}
            <UIRulesAssistant
                open={showAssistant}
                onClose={() => setShowAssistant(false)}
                initialRules={tableUiRules}
                onSave={setTableUiRules}
            />
        </div>
    );
}
